// src/pages/TradeTracker/index.jsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useProfile } from 'context/ProfileContext';
import { useAppAccess } from 'context/AppAccessContext';
import { useDialog } from 'context/DialogContext';
import useTradesData from './utils/useTradesData';

import Controls from './Controls';
import KpiCards from './KpiCards';
import TradesTable from './TradesTable';
import PortfolioChart from './PortfolioChart';

import { norm, canon, fullUSD, isCommodityTrade, unitForMax } from './utils/formatters';
import exportToExcel from 'utils/exportExcel';
import exportTradesToPDF from 'utils/exportPDF';
import dayjs from 'lib/dayjs';
import Page from 'components/Page';
import SectionCard from 'components/SectionCard';
import { logAudit } from 'lib/audit';

export default function TradeTracker() {
  const plotRef = useRef(null); // used for PDF chart capture

  const { profile } = useProfile();
  const isAdmin = !!profile?.is_admin;

  const { alert } = useDialog();

  const { apps, loading: appsLoading } = useAppAccess();
  const appList = Array.isArray(apps) ? apps : [];
  const hasAccess = !!(profile?.is_admin || appList.includes('trade-tracker'));

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedParty, setSelectedParty] = useState('');
  const [chartMode, setChartMode] = useState('valuation');
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const {
    trades = [],
    tradesById = new Map(),
    valuations = [],
    availableOrgs = [],
    availableParties = [],
    selectedDate,
    setSelectedDate,
    loading,
    error: err,
    portfolioSeries = [],
    seriesFrom,
    seriesTo,
    loadSeriesRange,
  } = useTradesData(profile, isAdmin, selectedOrg?.id, selectedParty);

  // Auto-select sole org
  useEffect(() => {
    if (!selectedOrg && availableOrgs.length === 1) {
      setSelectedOrg(availableOrgs[0]);
    }
  }, [availableOrgs, selectedOrg]);

  // Reset invalid party selection
  useEffect(() => {
    if (
      selectedParty &&
      !availableParties.find((p) => p.canon_name === selectedParty)
    ) {
      setSelectedParty('');
    }
  }, [availableParties, selectedParty]);

  // Valuation map keyed by normalized trade_id for the selected valuation date
  const valuationByTradeId = useMemo(() => {
    const map = new Map();
    for (const v of valuations) {
      if (v.valuation_date !== selectedDate) continue;
      map.set(norm(v.trade_id), v);
    }
    return map;
  }, [valuations, selectedDate]);

  // Filter trades by selectedParty (leg1_party)
  const filteredTrades = useMemo(() => {
    return trades.filter((t) => {
      if (selectedParty && canon(t.leg1_party) !== canon(selectedParty)) return false;
      return true;
    });
  }, [trades, selectedParty]);

  // Filter valuations to those tied to filtered trades
  const filteredValuations = useMemo(() => {
    const out = [];
    for (const [tid, v] of valuationByTradeId.entries()) {
      const trow = tradesById.get(tid);
      if (!trow) continue;
      if (selectedParty && canon(trow.leg1_party) !== canon(selectedParty)) continue;
      out.push(v);
    }
    return out;
  }, [valuationByTradeId, tradesById, selectedParty]);

  // KPI summary for the cards & exports
  const summary = useMemo(() => {
    let totalVal = 0;
    let totalNotional = 0;
    let totalPv01 = 0;

    for (const v of filteredValuations) {
      totalVal += Number(v.total_value || 0);
      totalNotional += Number(v.notional_amount || 0);
      totalPv01 += Number(v.pv01 || 0);
    }

    return { totalVal, totalNotional, totalPv01 };
  }, [filteredValuations]);

  const totalNotionalIsCommodity = useMemo(() => {
    if (!filteredValuations.length) return false;
    return filteredValuations.every((v) => {
      const trade = tradesById.get(norm(v.trade_id));
      return trade && isCommodityTrade(trade.trade_type);
    });
  }, [filteredValuations, tradesById]);

  // Chart data – already prebuilt by the hook
  const chartData = portfolioSeries;

  // For the chart scaling
  const maxAbs = useMemo(() => {
    let m = 0;
    for (const d of chartData) {
      const val = chartMode === 'valuation' ? d.valuation : d.notional;
      const a = Math.abs(val || 0);
      if (a > m) m = a;
    }
    return m;
  }, [chartData, chartMode]);

  const unit = useMemo(() => unitForMax(maxAbs), [maxAbs]);

  const notionalAllUSD = useMemo(() => {
    if (!filteredTrades.length) return false;
    const units = new Set(
      filteredTrades
        .map((t) => (t?.notional_units || '').toString().trim().toUpperCase())
        .filter(Boolean),
    );
    return units.size === 1 && units.has('USD');
  }, [filteredTrades]);

  // Chart series for Excel exports
  const chartExportSeries = useMemo(() => {
    if (!chartData?.length) return [];
    return chartData.map((d) => ({
      date: dayjs(d.ts).format('YYYY-MM-DD'),
      valuation: Number(d.valuation || 0),
      notional: Number(d.notional || 0),
    }));
  }, [chartData]);

  const Spinner = () => (
    <div className="flex items-center justify-center p-6">
      <div className="animate-spin h-5 w-5 border-4 border-muted border-t-brand rounded-full" />
    </div>
  );

  // Export Handlers
  const onExcelExport = async () => {
    try {
      if (!filteredTrades.length) return;

      const headers = [
        'Trade ID',
        'Trade Type',
        'Trade Party',
        'Counterparty',
        'Trade Date',
        'Eff Date',
        'Term Date',
        'Notional',
        'Value',
      ];

      const rows = filteredTrades.map((t) => {
        const tid = norm(t.trade_id);
        const v = valuationByTradeId.get(tid);

        return {
          'Trade ID': t.trade_id ?? '',
          'Trade Type': t.trade_type ?? '',
          'Trade Party': t.leg1_party ?? '',
          Counterparty: t.leg2_party ?? '',
          'Trade Date': t.trade_date ? new Date(t.trade_date) : '',
          'Eff Date': t.effective_date ? new Date(t.effective_date) : '',
          'Term Date': t.termination_date ? new Date(t.termination_date) : '',
          Notional: v?.notional_amount != null ? Number(v.notional_amount) : null,
          Value: v?.total_value != null ? Number(v.total_value) : null,
        };
      });

      const safeOrg = (selectedOrg?.name || 'Org').replace(/[^A-Za-z0-9]/g, '_');
      const asOfDate = selectedDate || new Date().toISOString().slice(0, 10);

      exportToExcel({
        sheetName: 'TradeTracker',
        fileName: `TradeTracker_${safeOrg}_${dayjs(asOfDate).format('YYYY-MM-DD')}.xlsx`,
        headers,
        rows,
        title: 'Fair Market Value Report',
        subtitle: `${selectedOrg?.name || ''} — Valuation data as of ${dayjs(asOfDate).format(
          'M/D/YYYY',
        )}`,
        createdStamp: new Date(),
        summary,
        chartSeries: chartExportSeries,
      });

      const now = new Date();
      await logAudit({
        category: 'export',
        action: 'excel_trades',
        success: true,
        actor_user_id: profile?.id,
        detail: {
          asOfDate,
          totalTrades: filteredTrades.length,
          createdStampUTC: now.toISOString(),
          createdStampDisplay: new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short',
          }).format(now),
        },
      });
    } catch (err) {
      await logAudit({
        category: 'export',
        action: 'excel_trades',
        success: false,
        actor_user_id: profile?.id,
        detail: { error: String(err) },
      });

      await alert(`Excel export failed: ${err?.message || String(err)}`, {
        title: 'Export failed',
        okLabel: 'OK',
      });
    }
  };

  const onPdfExport = async () => {
    try {
      if (!filteredTrades.length) return;

      setIsPdfGenerating(true);

      const asOfDate = selectedDate || new Date().toISOString().slice(0, 10);

      await exportTradesToPDF({
        filteredTrades,
        valuationByTradeId,
        selectedDate: asOfDate,
        orgName: selectedOrg?.name || '',
        fullUSD,
        createdStamp: new Date(),
        summary,
        totalNotionalIsCommodity,
        plotRef, // used for chart screenshot in PDF
        chartData,
        chartMode,
        notionalAllUSD,
      });

      const now = new Date();
      await logAudit({
        category: 'export',
        action: 'pdf_trades',
        success: true,
        actor_user_id: profile?.id,
        detail: {
          asOfDate,
          totalTrades: filteredTrades.length,
          createdStampUTC: now.toISOString(),
          createdStampDisplay: new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short',
          }).format(now),
        },
      });
    } catch (err) {
      await logAudit({
        category: 'export',
        action: 'pdf_trades',
        success: false,
        actor_user_id: profile?.id,
        detail: { error: String(err) },
      });

      await alert(`PDF export failed: ${err?.message || String(err)}`, {
        title: 'Export failed',
        okLabel: 'OK',
      });
    } finally {
      setIsPdfGenerating(false);
    }
  };

  // LOADING / ERROR / ACCESS
  if (appsLoading) {
    return (
      <Page title="TradeTracker">
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin h-10 w-10 border-4 border-muted border-t-brand rounded-full" />
        </div>
      </Page>
    );
  }

  if (err) {
    return (
      <Page title="TradeTracker">
        <div className="p-6 text-destructive">Error: {String(err)}</div>
      </Page>
    );
  }

  if (!hasAccess) {
    return (
      <Page title="TradeTracker">
        <div className="p-6 text-muted">You do not have access to this app.</div>
      </Page>
    );
  }

  return (
    <Page title="TradeTracker">
      {/* Filters */}
      <SectionCard title="Filters">
        <div className="relative">
          <Controls
            availableOrgs={availableOrgs}
            selectedOrg={selectedOrg}
            setSelectedOrg={setSelectedOrg}
            availableParties={availableParties}
            selectedParty={selectedParty}
            setSelectedParty={setSelectedParty}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            disabled={loading}
          />
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-app/50">
              <div className="animate-spin h-5 w-5 border-4 border-muted border-t-brand rounded-full" />
            </div>
          )}
        </div>
      </SectionCard>

      {selectedOrg && (
        <SectionCard title="Summary">
          {/* Top row: as-of text on left, export buttons on right */}
          <div className="flex flex-col gap-2 mt-1 mb-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-1">
              <div className="text-sm text-muted">Market data as of</div>
              <div className="text-sm font-medium">{selectedDate || '—'}</div>
              <div className="text-sm font-medium">end of day</div>
            </div>

            {filteredTrades.length > 0 && (
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={onExcelExport}
                  className="btn btn-primary text-sm"
                  title="Export current table view to Excel"
                >
                  Export Excel
                </button>
                <button
                  type="button"
                  onClick={onPdfExport}
                  className="btn btn-primary text-sm"
                  title="Export current view to PDF"
                  disabled={isPdfGenerating}
                >
                  {isPdfGenerating ? 'Generating PDF…' : 'Export PDF'}
                </button>
              </div>
            )}
          </div>

          {trades.length === 0 && loading ? (
            // Initial load only – safe to show spinner and no chart yet
            <Spinner />
          ) : trades.length > 0 ? (
            // Once we have trades, keep the chart mounted even if loading toggles
            <div className="space-y-8">
              <KpiCards
                selectedDate={selectedDate}
                summary={summary}
                hasRows={filteredValuations.length > 0}
                fullUSD={fullUSD}
                totalNotionalIsCommodity={totalNotionalIsCommodity}
              />
              <PortfolioChart
                plotRef={plotRef}
                chartData={chartData}
                chartMode={chartMode}
                setChartMode={setChartMode}
                maxAbs={maxAbs}
                unit={unit}
                notionalAllUSD={notionalAllUSD}
                endPrefillDate={selectedDate}
                seriesFrom={seriesFrom}
                seriesTo={seriesTo}
                onRangeChange={(from, to) => {
                  const tradeIds = filteredTrades.map((t) => t.trade_id);
                  if (from && to) {
                    loadSeriesRange(from, to, tradeIds);
                  }
                }}
              />
              <TradesTable
                selectedDate={selectedDate}
                filteredTrades={filteredTrades}
                valuationByTradeId={valuationByTradeId}
                fullUSD={fullUSD}
                isGenerating={isPdfGenerating}
              />
            </div>
          ) : (
            <div className="p-6 text-muted">No data</div>
          )}
        </SectionCard>
      )}
    </Page>
  );
}
